import { takeLatest, put, call, fork, select } from 'redux-saga/effects';
import {
  DO_APPROVE_NPWP,
  DO_REJECT_NPWP,
  DO_SET_NPWP_STATUS_PENDING,
  LOAD_NPWP_BUSINESS_DETAIL,
  LOAD_NPWP_BUSINESS_LIST,
  SUBMIT_EDIT_NPWP,
} from 'state/actionTypes';
import {
  appendNpwpDetailErrorMessages,
  clearNpwpApprovalLogs,
  clearNpwpDetailErrorMessages,
  resetNpwpDetailLoadingStates,
  setNpwpApproveModalLoading,
  setNpwpApproveModalShown,
  setNpwpBusinesListIsLoading,
  setNpwpBusinessesList,
  setNpwpDetailApprovalLogs,
  setNpwpDetailBusinessDetails,
  setNpwpDetailLoadingStates,
  setNpwpDetailModalShown,
  setNpwpDetailNpwpDetails,
  setNpwpListFilters,
  loadNpwpBusinessList as loadNpwpBusinessListAction,
  setNpwpRejectModalLoading,
  setNpwpRejectModalShown,
  setNpwpReviewDetailsModalLoading,
  setNpwpReviewDetailsModalShown,
  setNpwpEditModalLoading,
  setNpwpEditModalShown,
} from 'state/npwp/actions';
import * as api from 'api/kyb-npwp';
import * as toastActions from 'state/toast/actions';
import {
  APPROVED,
  PENDING,
  REJECTED,
} from 'constants/kybNpwp/parameterConstants';

export function* loadNpwpBusinessesList({ payload }) {
  const { filters, paging } = payload;
  yield put(setNpwpBusinesListIsLoading(true));
  try {
    yield put(setNpwpListFilters(filters));
    const response = yield call(api.getBusinessesList, paging, filters);
    const { data, paging: responsePaging } = response.data;
    yield put(setNpwpBusinessesList(data, responsePaging));
  } catch (e) {
    yield put(
      toastActions.errorMessage('Load business list failed', e.message)
    );
  } finally {
    yield put(setNpwpBusinesListIsLoading(false));
  }
}

function* fetchBusinessDetails(businessId) {
  yield put(
    setNpwpDetailLoadingStates({
      business: true,
    })
  );
  yield put(setNpwpDetailBusinessDetails(null));
  try {
    const { data: responseData } = yield call(
      api.getBusinessDetails,
      businessId
    );
    yield put(setNpwpDetailBusinessDetails(responseData.data));
  } catch (e) {
    yield put(
      appendNpwpDetailErrorMessages(e.message.map(m => `Business Detail: ${m}`))
    );
  } finally {
    yield put(
      setNpwpDetailLoadingStates({
        business: false,
      })
    );
  }
}

function* fetchNpwpDetails(businessId) {
  yield put(
    setNpwpDetailLoadingStates({
      npwp: true,
    })
  );
  yield put(setNpwpDetailNpwpDetails(null));
  try {
    const { data: responseData } = yield call(
      api.getBusinessIdentities,
      businessId
    );
    const npwp = {
      number: responseData.data.NPWP_NUMBER,
      name: responseData.data.NPWP_NAME,
      image: responseData.data.NPWP_IMAGE_URL,
    };
    yield put(setNpwpDetailNpwpDetails(npwp));
  } catch (e) {
    yield put(appendNpwpDetailErrorMessages(e.message.map(m => `NPWP: ${m}`)));
  } finally {
    yield put(
      setNpwpDetailLoadingStates({
        npwp: false,
      })
    );
  }
}

function* fetchNpwpLogs(businessId) {
  yield put(
    setNpwpDetailLoadingStates({
      log: true,
    })
  );
  yield put(clearNpwpApprovalLogs());
  try {
    const { data: responseData } = yield call(api.getNpwpLogs, businessId);
    yield put(setNpwpDetailApprovalLogs(responseData.data));
  } catch (e) {
    yield put(appendNpwpDetailErrorMessages(e.message.map(m => `Logs: ${m}`)));
  } finally {
    yield put(
      setNpwpDetailLoadingStates({
        log: false,
      })
    );
  }
}

export function* loadNpwpBusinessDetail({ payload }) {
  const { businessId } = payload;
  yield put(setNpwpDetailModalShown(true));
  yield put(clearNpwpDetailErrorMessages());

  yield put(resetNpwpDetailLoadingStates());
  yield put(clearNpwpApprovalLogs());
  yield fork(fetchNpwpDetails, businessId);
  yield call(fetchBusinessDetails, businessId);
  yield call(fetchNpwpLogs, businessId);
}

function* reloadNpwpList() {
  const {
    businesses: { data: paging, filters },
  } = yield select(state => state.npwp);
  yield put(loadNpwpBusinessListAction(filters, paging));
}

export function* doApproveNpwp({ payload }) {
  const { businessId } = payload;
  yield put(setNpwpApproveModalLoading(true));
  yield put(clearNpwpDetailErrorMessages());
  try {
    yield call(api.updateNpwpStatus, businessId, APPROVED);
  } catch (e) {
    yield put(
      appendNpwpDetailErrorMessages(e.message.map(m => `Approval: ${m}`))
    );
  } finally {
    yield put(setNpwpApproveModalLoading(false));
    yield put(setNpwpApproveModalShown(false));
  }
  yield put(resetNpwpDetailLoadingStates());
  yield put(clearNpwpApprovalLogs());
  yield fork(reloadNpwpList);
  yield call(fetchBusinessDetails, businessId);
  yield call(fetchNpwpLogs, businessId);
}

export function* doRejectNpwp({ payload }) {
  const { businessId, reason } = payload;
  yield put(setNpwpRejectModalLoading(true));
  yield put(clearNpwpDetailErrorMessages());
  try {
    yield call(api.updateNpwpStatus, businessId, REJECTED, reason);
  } catch (e) {
    yield put(
      appendNpwpDetailErrorMessages(e.message.map(m => `Rejection: ${m}`))
    );
  } finally {
    yield put(setNpwpRejectModalLoading(false));
    yield put(setNpwpRejectModalShown(false));
  }
  yield put(resetNpwpDetailLoadingStates());
  yield put(clearNpwpApprovalLogs());
  yield fork(reloadNpwpList);
  yield call(fetchBusinessDetails, businessId);
  yield call(fetchNpwpLogs, businessId);
}

export function* doSetNpwpStatusPending({ payload }) {
  const { businessId } = payload;
  yield put(setNpwpReviewDetailsModalLoading(true));
  yield put(clearNpwpDetailErrorMessages());
  try {
    yield call(api.updateNpwpStatus, businessId, PENDING);
  } catch (e) {
    yield put(
      appendNpwpDetailErrorMessages(e.message.map(m => `Update Details: ${m}`))
    );
  } finally {
    yield put(setNpwpReviewDetailsModalLoading(false));
    yield put(setNpwpReviewDetailsModalShown(false));
  }
  yield put(resetNpwpDetailLoadingStates());
  yield put(clearNpwpApprovalLogs());
  yield fork(reloadNpwpList);
  yield call(fetchBusinessDetails, businessId);
  yield call(fetchNpwpLogs, businessId);
}

export function* submitEditNpwp({ payload }) {
  const { businessId, form } = payload;
  yield put(setNpwpEditModalLoading(true));
  yield put(clearNpwpDetailErrorMessages());
  try {
    yield call(api.updateNpwpDetails, businessId, form);
  } catch (e) {
    yield put(
      appendNpwpDetailErrorMessages(e.message.map(m => `Update NPWP: ${m}`))
    );
  } finally {
    yield put(setNpwpEditModalLoading(false));
    yield put(setNpwpEditModalShown(false));
  }
  yield put(resetNpwpDetailLoadingStates());
  yield put(clearNpwpApprovalLogs());
  yield fork(fetchNpwpDetails, businessId);
  yield call(fetchNpwpLogs, businessId);
}

export default function*() {
  yield takeLatest(LOAD_NPWP_BUSINESS_LIST, loadNpwpBusinessesList);
  yield takeLatest(LOAD_NPWP_BUSINESS_DETAIL, loadNpwpBusinessDetail);
  yield takeLatest(DO_APPROVE_NPWP, doApproveNpwp);
  yield takeLatest(DO_REJECT_NPWP, doRejectNpwp);
  yield takeLatest(DO_SET_NPWP_STATUS_PENDING, doSetNpwpStatusPending);
  yield takeLatest(SUBMIT_EDIT_NPWP, submitEditNpwp);
}
